export interface GeoCoords {
  lat: number
  lng: number
  label?: string
}

const GEO_URI = /geo:([-\d.]+),([-\d.]+)/
const GOOGLE_MAPS_LL = /google\.\w+\/maps[^"'\s]*[@/](-?\d+\.?\d*),(-?\d+\.?\d*)/
const GOOGLE_MAPS_Q = /google\.\w+\/maps\?q=([-\d.]+),([-\d.]+)/
const APPLE_MAPS = /maps\.apple\.com[^"'\s]*[?&]ll=([-\d.]+),([-\d.]+)/
const OSM_LINK = /openstreetmap\.org[^"'\s]*#map=\d+\/([-\d.]+)\/([-\d.]+)/
const COORDS_PATTERN = /(-?\d{1,3}\.\d{3,}),\s*(-?\d{1,3}\.\d{3,})/

const ADDRESS_PATTERNS = [
  /\b(?:on|at|near)\s+(?:the\s+)?([A-Z][\w' ]+?(?:\s+(?:Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Drive|Dr|Highway|Hwy|Lane|Ln|Place|Pl|Bridge|Parkway|Pkwy|Expressway|Expy|Turnpike|Tpke))\b(?:\s+(?:at|and|&|near)\s+[A-Z][\w' ]+?(?:\s+(?:Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Drive|Dr|Highway|Hwy|Lane|Ln|Place|Pl))\b)?(?:\s+in\s+[A-Z][\w ]+?)?)/,
  /\b(\d+\s+[A-Z][\w' ]+?\s+(?:Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Drive|Dr|Highway|Hwy|Lane|Ln|Place|Pl)(?:,?\s+[A-Z][\w ]+)?)\b/,
]

const geocodeCache = new Map<string, GeoCoords | null>()

function cleanAddressForGeocode(address: string): string {
  const inMatch = address.match(/\bin\s+(.+)$/)
  const city = inMatch?.[1]?.trim()

  let street = inMatch ? address.slice(0, inMatch.index).trim() : address
  street = street.replace(/\s+(?:at|and|&|near)\s+.*/i, '')
  street = street.replace(/\bthe\b/gi, '').replace(/\s+/g, ' ').trim()

  return city ? `${street}, ${city}` : street
}

export async function geocodeAddress(query: string): Promise<GeoCoords | null> {
  const cleaned = cleanAddressForGeocode(query)
  const cached = geocodeCache.get(cleaned)
  if (cached !== undefined) return cached

  try {
    const res = await fetch(`/api/geocode?q=${encodeURIComponent(cleaned)}`)
    if (!res.ok) throw new Error()
    const data = await res.json() as { lat: string; lon: string; display_name: string }[]
    if (!data.length) {
      geocodeCache.set(cleaned, null)
      return null
    }
    const result = { lat: parseFloat(data[0]!.lat), lng: parseFloat(data[0]!.lon), label: query }
    geocodeCache.set(cleaned, result)
    return result
  } catch {
    geocodeCache.set(cleaned, null)
    return null
  }
}

export async function aiLocate(text: string, userId?: string | null): Promise<GeoCoords | null> {
  const cached = geocodeCache.get(`ai:${text.slice(0, 100)}`)
  if (cached !== undefined) return cached

  try {
    const userParam = userId ? `&user=${encodeURIComponent(userId)}` : ''
    const res = await fetch(`/api/ai-locate?text=${encodeURIComponent(text.slice(0, 1000))}${userParam}`)
    if (!res.ok) throw new Error()
    const data = await res.json() as { found: boolean; lat?: number; lng?: number; label?: string }
    if (!data.found || !data.lat || !data.lng) {
      geocodeCache.set(`ai:${text.slice(0, 100)}`, null)
      return null
    }
    const result = { lat: data.lat, lng: data.lng, label: data.label }
    geocodeCache.set(`ai:${text.slice(0, 100)}`, result)
    return result
  } catch {
    geocodeCache.set(`ai:${text.slice(0, 100)}`, null)
    return null
  }
}

export function extractLocationAddress(text: string): string | null {
  for (const pattern of ADDRESS_PATTERNS) {
    const match = text.match(pattern)
    if (match?.[1] && match[1].length >= 10) {
      return match[1].trim()
    }
  }
  return null
}

export function extractLocation(content: Record<string, unknown>): GeoCoords | null {
  if (content.msgtype === 'm.location') {
    const geoUri = content.geo_uri as string | undefined
    if (geoUri) {
      const match = geoUri.match(GEO_URI)
      if (match) {
        return { lat: parseFloat(match[1]!), lng: parseFloat(match[2]!), label: content.body as string }
      }
    }
  }

  const body = (content.body as string) ?? ''
  if (!body) return null

  const geoMatch = body.match(GEO_URI)
  if (geoMatch) return { lat: parseFloat(geoMatch[1]!), lng: parseFloat(geoMatch[2]!) }

  for (const pattern of [GOOGLE_MAPS_Q, GOOGLE_MAPS_LL, APPLE_MAPS, OSM_LINK]) {
    const match = body.match(pattern)
    if (match) return { lat: parseFloat(match[1]!), lng: parseFloat(match[2]!) }
  }

  const coordMatch = body.match(COORDS_PATTERN)
  if (coordMatch) {
    const lat = parseFloat(coordMatch[1]!)
    const lng = parseFloat(coordMatch[2]!)
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng }
    }
  }

  return null
}
